// src/services/settingsPhotoServices.js
import axios from "axios";

// Compat Vite & CRA
const _base =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_URL) ||
  process.env.REACT_APP_API_URL ||
  "";

const baseUrl = _base.replace(/\/$/, "");
const apiUrl = `${baseUrl}/api/settings`;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Helper: URL absolue pour <img src=...>
 * - Si /uploads/... => préfixe par baseUrl
 * - Sinon renvoie tel quel (ex: Cloudinary https://...)
 */
export const toAbsolutePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (photoUrl.startsWith("/uploads/")) return `${baseUrl}${photoUrl}`;
  return photoUrl;
};

/* =========================
   Upload (par endpoint)
   ========================= */

/**
 * Upload logo club
 * Back route: PATCH /api/settings/updateLogoClub
 * Champ multipart: "logoClub"
 * Retour back: { id, field, url, masterUrl? } (selon ton controller)
 */
export const uploadSettingsLogoClub = async (file, options = {}) => {
  if (!file) throw new Error("file requis");

  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  form.append("logoClub", file);

  const qs = format ? `?format=${encodeURIComponent(format)}` : "";

  try {
    const { data } = await axios.patch(`${apiUrl}/updateLogoClub${qs}`, form, {
      headers: {
        ...authHeaders(),
        // axios gère le Content-Type multipart automatiquement
      },
      onUploadProgress,
    });
    return data;
  } catch (error) {
    console.error("[uploadSettingsLogoClub] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

// ✅ Header 1
export const uploadSettingsHeader1 = async (file, options = {}) => {
  if (!file) throw new Error("file requis");
  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  // ⚠️ doit matcher settingsRoutes : upload("settings").single("firstHeaderImage")
  form.append("firstHeaderImage", file);

  const qs = format ? `?format=${encodeURIComponent(format)}` : "";

  try {
    const { data } = await axios.patch(
      `${apiUrl}/updateFirstHeaderImage${qs}`,
      form,
      {
        headers: { ...authHeaders() },
        onUploadProgress,
      },
    );
    return data;
  } catch (error) {
    console.error("[uploadSettingsHeader1] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

// ✅ Header 2
export const uploadSettingsHeader2 = async (file, options = {}) => {
  if (!file) throw new Error("file requis");
  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  // ⚠️ doit matcher settingsRoutes : upload("settings").single("secondHeaderImage")
  form.append("secondHeaderImage", file);

  const qs = format ? `?format=${encodeURIComponent(format)}` : "";

  try {
    const { data } = await axios.patch(
      `${apiUrl}/updateSecondHeaderImage${qs}`,
      form,
      {
        headers: { ...authHeaders() },
        onUploadProgress,
      },
    );
    return data;
  } catch (error) {
    console.error("[uploadSettingsHeader2] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

// ✅ Default image licence/cotisation
export const uploadSettingsDefaultLicCot = async (file, options = {}) => {
  if (!file) throw new Error("file requis");
  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  // ⚠️ doit matcher settingsRoutes : single("defaultLicCotImage")
  form.append("defaultLicCotImage", file);

  const qs = format ? `?format=${encodeURIComponent(format)}` : "";

  try {
    const { data } = await axios.patch(
      `${apiUrl}/updateDefaultLicCotImage${qs}`,
      form,
      {
        headers: { ...authHeaders() },
        onUploadProgress,
      },
    );
    return data; // idéalement { url: "..." }
  } catch (error) {
    console.error("[uploadSettingsDefaultLicCot] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

// ✅ Règlement intérieur — Header
export const uploadReglementHeaderImage = async (file, options = {}) => {
  if (!file) throw new Error("file requis");
  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  form.append("reglementHeaderImage", file); // ⚠️ must match .single(...)

  const qs = format ? `?format=${encodeURIComponent(format)}` : "";

  try {
    const { data } = await axios.patch(
      `${apiUrl}/updateReglementHeaderImage${qs}`,
      form,
      { headers: { ...authHeaders() }, onUploadProgress },
    );
    return data;
  } catch (error) {
    console.error("[uploadReglementHeaderImage] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

// ✅ Règlement intérieur — First image
export const uploadReglementFirstImage = async (file, options = {}) => {
  if (!file) throw new Error("file requis");
  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  form.append("reglementFirstImage", file);

  const qs = format ? `?format=${encodeURIComponent(format)}` : "";

  try {
    const { data } = await axios.patch(
      `${apiUrl}/updateReglementFirstImage${qs}`,
      form,
      { headers: { ...authHeaders() }, onUploadProgress },
    );
    return data;
  } catch (error) {
    console.error("[uploadReglementFirstImage] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

// ✅ Règlement intérieur — Second image
export const uploadReglementSecondImage = async (file, options = {}) => {
  if (!file) throw new Error("file requis");
  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  form.append("reglementSecondImage", file);

  const qs = format ? `?format=${encodeURIComponent(format)}` : "";

  try {
    const { data } = await axios.patch(
      `${apiUrl}/updateReglementSecondImage${qs}`,
      form,
      { headers: { ...authHeaders() }, onUploadProgress },
    );
    return data;
  } catch (error) {
    console.error("[uploadReglementSecondImage] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/* =========================
   Recrop (optionnel)
   ========================= */

/**
 * Recrop d'un champ Settings (utile surtout logo_club).
 * Back route: POST /api/settings/:id/crop/:field
 * Body JSON: { x, y, width, height, src?, format? }
 *
 * @param {number|string} settingsId
 * @param {string} field - ex: "logo_club"
 * @param {{x:number,y:number,width:number,height:number,src?:string,format?:"webp"|"jpg"|"png"}} crop
 */
export const cropSettingsPhoto = async (settingsId, field, crop = {}) => {
  if (!settingsId) throw new Error("settingsId requis");
  if (!field) throw new Error("field requis");

  const { x, y, width, height, src, format } = crop;
  const nums = [x, y, width, height];
  if (nums.some((v) => typeof v !== "number" || Number.isNaN(v))) {
    throw new Error("x, y, width, height numériques requis");
  }

  const payload = { x, y, width, height };
  if (src) payload.src = src;
  if (format) payload.format = format;

  try {
    const { data } = await axios.post(
      `${apiUrl}/${encodeURIComponent(settingsId)}/crop/${encodeURIComponent(field)}`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      },
    );
    return data;
  } catch (error) {
    console.error("[cropSettingsPhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};
